import React from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ForcePasswordChange from '../pages/ForcePasswordChange';
import { 
  House, Car, Users, Calendar, Receipt, Gear, SignOut, 
  List, X, Presentation, FileText, MapPin, Tag, Warning, Vault, ChatText, WhatsappLogo, Wrench, Buildings, Ticket, UserPlus, Envelope, Timer, EnvelopeOpen
} from '@phosphor-icons/react';
import { useState } from 'react';
import {pageAvailable} from '../lib/instanceFeatures';
import QrScannerButton from './QrScannerButton';

const navItems = [
  { path: '/anbieter', icon: Buildings, label: 'Anbieter-Verwaltung', providerOnly: true },
  { path: '/dashboard', icon: House, label: 'Dashboard' },
  { path: '/vehicles', icon: Car, label: 'Fahrzeuge' },
  { path: '/customers', icon: Users, label: 'Kunden' },
  { path: '/customer-onboardings', icon: UserPlus, label: 'Kunden-Anfragen' },
  { path: '/inquiries', icon: ChatText, label: 'Anfragen' },
  { path: '/messages', icon: WhatsappLogo, label: 'Nachrichten' },
  { path: '/emails', icon: Envelope, label: 'E-Mails' },
  { path: '/bookings', icon: Calendar, label: 'Buchungen' },
  { path: '/long-term', icon: Timer, label: 'Langzeitmiete' },
  { path: '/calendar', icon: Calendar, label: 'Kalender' },
  { path: '/maintenance', icon: Wrench, label: 'Wartung' },
  { path: '/invoices', icon: Receipt, label: 'Finanzen', roles: ['admin', 'employee', 'accountant'] },
  { path: '/dunning', icon: EnvelopeOpen, label: 'Mahnwesen', roles: ['admin', 'employee', 'accountant'] },
  { path: '/datev', icon: FileText, label: 'DATEV-Export', roles: ['admin', 'employee', 'accountant'] },
  { path: '/cashbook', icon: Vault, label: 'Kassenbuch', roles: ['admin', 'employee', 'accountant'] },
  { path: '/deposits', icon: Vault, label: 'Kautionen', roles: ['admin', 'employee', 'accountant'] },
  { path: '/supplier-settlements', icon: Buildings, label: 'Lieferanten-Abrechnung', roles: ['admin', 'employee', 'accountant'] },
  { path: '/tickets', icon: Ticket, label: 'Strafzettel' },
  { path: '/letters', icon: Envelope, label: 'Briefe' },
  { path: '/accidents', icon: FileText, label: 'Unfallmeldungen', permission: 'damages.view', roles: ['admin', 'employee'] },
  { path: '/documents', icon: FileText, label: 'Dokumente', permission: 'documents.view', roles: ['admin', 'employee'] },
  { path: '/inbox', icon: EnvelopeOpen, label: 'Posteingang' },
  { path: '/quotes', icon: FileText, label: 'Angebote' },
  { path: '/damages', icon: Warning, label: 'Schäden' },
  { path: '/categories', icon: Tag, label: 'Kategorien' },
  { path: '/stations', icon: MapPin, label: 'Stationen' },
  { path: '/reports', icon: Presentation, label: 'Berichte' },
  { path: '/settings', icon: Gear, label: 'Einstellungen' }
];

// Für Buchhalter (accountant): nur diese Pfade sichtbar
const ACCOUNTANT_ALLOWED_PATHS = new Set([
  '/', '/invoices', '/dunning', '/datev', '/cashbook', '/deposits', '/supplier-settlements',
]);

const Layout = () => {
  const { user, logout, instance } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Zwangs-Passwort-Änderung: Nach Admin-Reset muss der User zuerst ein neues Passwort setzen
  if (user && user.requires_password_change) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E4E9] z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-[#525866] hover:bg-[#F7F8FA] rounded-sm"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X weight="bold" size={24} /> : <List weight="bold" size={24} />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#E60000] rounded-sm flex items-center justify-center">
            <Car weight="fill" className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111418]">AutoVermietung</span>
        </div>
        <QrScannerButton iconOnly />
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E2E4E9] z-50 transform transition-transform duration-200 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-[#E2E4E9] flex-shrink-0">
          <div className="w-10 h-10 bg-[#E60000] rounded-sm flex items-center justify-center">
            <Car weight="fill" className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-bold text-[#111418] block">AutoVermietung</span>
            <span className="text-xs text-[#8A94A6]">Management</span>
          </div>
        </div>

        {/* Navigation (scrollable) */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems
            .filter((item) => {
              if (item.providerOnly && !user?.provider_owner) return false;
              if (!pageAvailable(instance, item.path)) return false;
              if (user?.role === 'accountant') return ACCOUNTANT_ALLOWED_PATHS.has(item.path);
              if (item.roles && !item.roles.includes(user?.role)) return false;
              if (item.permission && user?.role === 'employee' && Array.isArray(user.permissions) && !user.permissions.includes(item.permission)) return false;
              return true;
            })
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight={({ isActive }) => isActive ? 'fill' : 'regular'} size={20} />
              {item.label}
            </NavLink>
          ))}
          {user?.role === 'accountant' && (
            <p className="text-[11px] text-[#8A94A6] px-3 pt-4 pb-1">Buchhalter-Zugang: nur Finanz-Bereiche sichtbar.</p>
          )}
        </nav>

        {/* User Info (sticky bottom) */}
        <div className="flex-shrink-0 p-4 border-t border-[#E2E4E9] bg-white">
          <div className="mb-3">
            <QrScannerButton className="w-full justify-center" label="QR scannen" />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#F7F8FA] rounded-sm flex items-center justify-center">
              <span className="font-bold text-[#525866]">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#111418] truncate">{user?.name}</p>
              <p className="text-xs text-[#8A94A6] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-[#E60000] hover:bg-red-50"
            data-testid="logout-btn"
          >
            <SignOut weight="bold" size={20} />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {instance?.managed && !instance.active ? <div className="p-8"><h1 className="text-2xl font-semibold">Zugang derzeit gesperrt</h1><p className="mt-3">Bitte wende dich an deinen Softwareanbieter.</p></div> : !pageAvailable(instance, location.pathname) ? <div className="p-8"><h1 className="text-2xl font-semibold">Option nicht freigeschaltet</h1><p className="mt-3">Dein Softwareanbieter kann diese Funktion für deine Firma aktivieren.</p></div> : <Outlet />}
      </main>
    </div>
  );
};

export default Layout;
